import * as React from 'react';
import { HeaderGroup } from '@tanstack/react-table';

import { NewTableColumn, NewTableDefaultDataItem, NewTableLoadMetadata, NewTableLoadMetadataSorting, NewTableSortingDirection } from '@common/components/new-table/new-table.types';

export type HeaderProps = {
  currentColumnResizeOffset?: number;
  loadTableMetadata?: NewTableLoadMetadata;
  model: Array<HeaderGroup<NewTableDefaultDataItem>>;
  columns: Array<NewTableColumn<NewTableDefaultDataItem>>;
  onColumnResizeEnd?: () => void;
  onSort: (sorting: NewTableLoadMetadataSorting) => Promise<void> | void;
  onCellContextMenu?: (params: { 
    columnId: NewTableColumn<NewTableDefaultDataItem>['id'];
    coords: {
      x: number;
      y: number;
    };
  }) => void;
  onHeaderContextMenu?: (params: { 
    coords: {
      x: number;
      y: number;
    };
  }) => void;
};

export const useController = (props: HeaderProps) => {
  const { loadTableMetadata, onSort, onCellContextMenu, onHeaderContextMenu } = props;

  const handleSort = React.useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
    const { id } = e.currentTarget.dataset as { id: string; };
    let nextSorting: NewTableLoadMetadataSorting;;

    if (loadTableMetadata?.sorting.id === id) {
      nextSorting = {
        id,
        direction: loadTableMetadata.sorting.direction === NewTableSortingDirection.desc 
          ? NewTableSortingDirection.asc
          : NewTableSortingDirection.desc
      };
    } else {
      nextSorting = {
        id,
        direction: loadTableMetadata?.sorting.direction || NewTableSortingDirection.asc
      };
    }

    onSort(nextSorting);
  }, [loadTableMetadata?.sorting, onSort]);

  const handleCellContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const { id } = e.currentTarget.dataset as { id: string; };

    if (onCellContextMenu) {
      onCellContextMenu({
        columnId: id,
        coords: {
          x: e.clientX,
          y: e.clientY,
        }
      });
    }
  }, [onCellContextMenu]);

  const handleHeaderContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableRowElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (onHeaderContextMenu) {
      onHeaderContextMenu({
        coords: {
          x: e.clientX,
          y: e.clientY,
        }
      });
    }
  }, [onHeaderContextMenu]);

  return {
    handleSort,
    handleCellContextMenu,
    handleHeaderContextMenu,
  };
};